/**
 * EditorPreview - Real-time 3D preview for the stage editor
 * Lightweight Babylon.js scene that shows how the stage will look
 */

import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Vector3, Matrix } from '@babylonjs/core/Maths/math.vector';
import '@babylonjs/core/Culling/ray';
import { DomeComponent } from '../src/scenes/DomeComponent';
import { ModelComponent } from '../src/scenes/ModelComponent';
import { PlaneComponent } from '../src/scenes/PlaneComponent';
import { HotspotComponent } from '../src/scenes/HotspotComponent';
import { FloorArrowManager } from '../src/scenes/FloorArrowManager';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { PointLight } from '@babylonjs/core/Lights/pointLight';
import { SpotLight } from '@babylonjs/core/Lights/spotLight';
import { Light } from '@babylonjs/core/Lights/light';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { PointerEventTypes } from '@babylonjs/core/Events/pointerEvents';
import type { ExperienceStage, SkyboxConfig, ModelConfig, PlaneConfig, LightConfig, HotspotConfig, Vector3Config } from '../src/ui/UIManager';
import type { MediaItem } from '../src/media/MediaConfig';
import { ModelEditorComponent, type TransformData, type MeshInfo, type ModelStats, type AnimationInfo, type AnimationState } from '../src/scenes/ModelEditorComponent';
import { PostProcessingManager } from '../src/effects/PostProcessingManager';
import type { PostProcessingConfig } from '../src/types/config';
import { Logger } from '../src/utils/Logger';

const logger = Logger.getLogger('EditorPreview');

export class EditorPreview {
  private canvas: HTMLCanvasElement;
  private engine: Engine;
  private scene: Scene;
  private camera: ArcRotateCamera;
  private sceneContentParent: TransformNode;
  private domeComponent: DomeComponent;
  private modelComponent: ModelComponent;
  private planeComponent: PlaneComponent;
  private hotspotComponent: HotspotComponent;
  private floorArrowManager: FloorArrowManager;
  private modelEditorComponent: ModelEditorComponent;
  private postProcessingManager: PostProcessingManager | null = null;
  private currentLights: Light[] = [];
  private isRendering: boolean = false;
  private loadingTimeout: ReturnType<typeof setTimeout> | null = null;
  private isInitialized: boolean = false;
  private hotspotCreationMode: boolean = false;
  private repositionHotspotId: string | null = null;
  private skyboxLoadGeneration: number = 0; // Tracks current load to prevent race conditions
  private modelLoadGeneration: number = 0; // Tracks model loads to prevent duplicates from race conditions

  // Camera target (shows where camera looks when scene loads) - only for first stage
  private cameraTargetMesh: Mesh | null = null;
  private cameraTargetDragMode: boolean = false;
  private currentStageIndex: number = 0;

  // Drag state for hotspots and camera target
  private draggingMesh: Mesh | null = null;
  private isDragging: boolean = false;
  private dragStartPointer: { x: number; y: number } | null = null;
  private dragThreshold: number = 5; // Pixels moved before considered a drag

  // Event listener references for cleanup
  private wheelHandler: ((event: WheelEvent) => void) | null = null;
  private touchStartHandler: ((event: TouchEvent) => void) | null = null;
  private touchMoveHandler: ((event: TouchEvent) => void) | null = null;
  private touchEndHandler: ((event: TouchEvent) => void) | null = null;
  private updateTimeout: ReturnType<typeof setTimeout> | null = null;

  public onHotspotCreated?: (position: { x: number; y: number; z: number }) => void;
  public onHotspotRepositioned?: (id: string, position: { x: number; y: number; z: number }) => void;
  public onHotspotClick?: (hotspotId: string) => void;
  public onCameraTargetMoved?: (position: { x: number; y: number; z: number }) => void;
  public onHotspotDragged?: (id: string, position: { x: number; y: number; z: number }) => void;
  public onViewDirectionTargetMoved?: (index: number, position: { x: number; y: number; z: number }) => void;
  public onModelTransformEnd?: (transform: TransformData) => void;
  public onPlaneTransformEnd?: (index: number, transform: { position: { x: number; y: number; z: number }; rotation: { x: number; y: number; z: number }; scale: { width: number; height: number } }) => void;
  public onHumanScalePositionChanged?: (position: { x: number; y: number; z: number }) => void;
  public onAnimationFrameUpdate?: (currentFrame: number, totalFrames: number) => void;

  // Track which plane is being edited with gizmo
  private editingPlaneIndex: number = -1;

  // Track what type of object is being edited with gizmo
  private editingType: 'none' | 'model' | 'plane' | 'humanScale' = 'none';

  constructor(canvasId: string) {
    this.canvas = document.getElementById(canvasId) as HTMLCanvasElement;
    
    if (!this.canvas) {
      logger.error(`Canvas with ID "${canvasId}" not found`);
      throw new Error(`Canvas with ID "${canvasId}" not found`);
    }

    this.engine = new Engine(this.canvas, true);
    this.scene = new Scene(this.engine);
    
    // Setup camera with human-realistic FOV and proper orbit distance for 3D editing
    // 60° FOV (~1.047 radians) approximates human central vision for natural perspective
    this.camera = new ArcRotateCamera(
      'camera',
      0, // Match viewer camera alpha
      Math.PI / 2.5,
      5, // Starting orbit distance - gives room to view 3D models
      new Vector3(0, 0, 0), // Pivot at origin
      this.scene
    );
    this.camera.attachControl(this.canvas, true);
    this.camera.minZ = 0.1;
    this.camera.maxZ = 10000; // Ensure we can see the dome (which uses infiniteDistance)
    this.camera.wheelPrecision = 50;

    // Set orbit distance limits for 3D model editing
    this.camera.lowerRadiusLimit = 0.5; // Close enough to inspect details
    this.camera.upperRadiusLimit = 50; // Far enough to see full scene

    // Invert camera drag direction to match viewer behavior
    // Reduce sensitivity (multiply by higher value = slower movement)
    this.camera.angularSensibilityX *= -1.875;
    this.camera.angularSensibilityY *= -1.875;

    // Set human-realistic 60° FOV (similar to 50mm camera lens)
    // Human eye: ~60° central vision, ~120° peripheral
    // 60° provides natural, undistorted perspective
    this.camera.fov = Math.PI / 3; // 60 degrees = π/3 radians ≈ 1.047

    // Setup FOV zoom controls
    this.setupFOVZoom();

    // Create parent node for all scene content (enables grouped rotation for skybox offset)
    this.sceneContentParent = new TransformNode('sceneContentParent', this.scene);

    // Initialize components
    this.domeComponent = new DomeComponent(this.scene);
    this.modelComponent = new ModelComponent(this.scene);
    this.planeComponent = new PlaneComponent(this.scene);
    this.hotspotComponent = new HotspotComponent(this.scene);
    this.floorArrowManager = new FloorArrowManager(this.scene, true); // isEditor = true
    this.modelEditorComponent = new ModelEditorComponent(this.scene);

    this.domeComponent.setParentNode(this.sceneContentParent);
    this.hotspotComponent.setParentNode(this.sceneContentParent);
    this.floorArrowManager.setParentNode(this.sceneContentParent);
    this.modelComponent.setParentNode(this.sceneContentParent);
    this.planeComponent.setParentNode(this.sceneContentParent);

    // Initialize post-processing manager for live effect preview
    // Must be created after camera exists since effects are applied to camera
    this.postProcessingManager = new PostProcessingManager(this.scene, this.camera);

    // Connect model editor callbacks
    this.modelEditorComponent.onGizmoTransformEnd = (transform) => {
      logger.debug('onGizmoTransformEnd - editingType:', this.editingType, 'transform:', transform);

      // Route callback based on what type of object is being edited
      switch (this.editingType) {
        case 'plane':
          // Plane editing - emit plane transform with width/height format
          this.emitPlaneTransformEnd();
          break;
        case 'model':
          // Model editing - emit model transform
          if (this.onModelTransformEnd) {
            this.onModelTransformEnd(transform);
          }
          break;
        case 'humanScale':
          // Human scale editing - position is updated in real-time via observable
          // No additional action needed on drag end
          logger.debug('Human scale gizmo drag ended');
          break;
        default:
          logger.warn('Gizmo transform ended but editingType is:', this.editingType);
      }
    };
    this.modelEditorComponent.onHumanScalePositionChanged = (position) => {
      if (this.onHumanScalePositionChanged) {
        this.onHumanScalePositionChanged(position);
      }
    };
    this.modelEditorComponent.onHumanScaleRotationChanged = (rotationY) => {
      if (this.onHumanScaleRotationChanged) {
        this.onHumanScaleRotationChanged(rotationY);
      }
    };
    this.modelEditorComponent.onAnimationFrameUpdate = (currentFrame, totalFrames) => {
      if (this.onAnimationFrameUpdate) {
        this.onAnimationFrameUpdate(currentFrame, totalFrames);
      }
    };

    // Setup lights
    const light = new HemisphericLight('light', new Vector3(0, 1, 0), this.scene);
    light.intensity = 0.7;
    this.currentLights.push(light);

    // Set default background color (dark gray) to distinguish from non-rendering
    this.scene.clearColor = new Color4(0.1, 0.1, 0.1, 1);

    // Handle window resize
    window.addEventListener('resize', () => {
      this.engine.resize();
    });

    // Handle container resize (for floating panels)
    // Watch the .resizable-panel wrapper (several levels up) for proper resize detection
    const resizablePanel = this.canvas.closest('.resizable-panel');
    if (resizablePanel) {
      const resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        // Only resize if panel has valid dimensions (not hidden)
        if (entry && entry.contentRect.width > 0 && entry.contentRect.height > 0) {
          this.engine.resize();
        }
      });
      resizeObserver.observe(resizablePanel);

      // Listen for panel-shown event to resize when panel becomes visible
      resizablePanel.addEventListener('panel-shown', () => {
        requestAnimationFrame(() => {
          this.engine.resize();
          logger.debug('Resized after panel-shown event');
        });
      });
    } else if (this.canvas.parentElement) {
      // Fallback: observe immediate parent
      const resizeObserver = new ResizeObserver(() => {
        this.engine.resize();
      });
      resizeObserver.observe(this.canvas.parentElement);
    }

    // Setup hotspot creation click handler
    this.setupHotspotClickHandler();

    // Setup hotspot interaction handler
    this.hotspotComponent.onHotspotClick = (hotspot: HotspotConfig) => {
      // Ignore clicks if in creation or reposition mode
      if (this.hotspotCreationMode || this.repositionHotspotId) return;
      
      if (this.onHotspotClick) {
        this.onHotspotClick(hotspot.id);
      }
    };

    logger.debug('Initialized');
  }

  /**
   * Setup click handler for hotspot creation, repositioning, camera target, and dragging
   */
  private setupHotspotClickHandler(): void {
    // POINTER DOWN - Start interaction or drag
    this.scene.onPointerObservable.add((pointerInfo) => {
      if (pointerInfo.type === PointerEventTypes.POINTERDOWN) {
        const pickResult = this.scene.pick(this.scene.pointerX, this.scene.pointerY);

        // Track pointer start for drag detection
        this.dragStartPointer = { x: this.scene.pointerX, y: this.scene.pointerY };
        this.isDragging = false;

        // Check if we clicked on a draggable mesh (camera target or hotspot)
        if (pickResult.hit && pickResult.pickedMesh) {
          const mesh = pickResult.pickedMesh;

          // DEBUG: Log what was picked
          logger.debug('POINTERDOWN picked:', {
            name: mesh.name,
            isPickable: mesh.isPickable,
            isVisible: mesh.isVisible,
            hasMetadata: !!mesh.metadata,
            metadataKeys: mesh.metadata ? Object.keys(mesh.metadata) : [],
            hasConfig: !!mesh.metadata?.config,
            configId: mesh.metadata?.config?.id,
          });

          // Camera target clicked - start drag
          if (mesh.metadata?.isCameraTarget) {
            this.draggingMesh = mesh as Mesh;
            this.canvas.style.cursor = 'grabbing';
            this.camera.detachControl();
            return;
          }

          // View direction target clicked - start drag
          if (mesh.metadata?.isViewDirectionTarget) {
            this.draggingMesh = mesh as Mesh;
            this.canvas.style.cursor = 'grabbing';
            this.camera.detachControl();
            return;
          }

          // Human scale reference clicked - start drag on ground plane
          if (mesh.metadata?.isHumanScaleReference) {
            logger.debug('Starting human scale drag');
            this.draggingMesh = mesh as Mesh;
            this.canvas.style.cursor = 'grabbing';
            this.camera.detachControl();
            return;
          }

          // Hotspot clicked - start drag (if not in special mode)
          if (!this.hotspotCreationMode && !this.repositionHotspotId && !this.cameraTargetDragMode) {
            const hotspotConfig = mesh.metadata?.config as HotspotConfig | undefined;
            if (hotspotConfig) {
              logger.debug('Starting drag for hotspot:', hotspotConfig.id);
              this.draggingMesh = mesh as Mesh;
              this.canvas.style.cursor = 'grabbing';
              // Detach camera controls so skybox doesn't move during drag
              this.camera.detachControl();
              return;
            } else {
              logger.debug('Mesh has no config, not starting drag');
            }
          }
        } else {
          logger.debug('POINTERDOWN: No mesh hit');
        }

        // Handle special modes (creation, repositioning, camera target placement)
        if (this.hotspotCreationMode || this.repositionHotspotId || this.cameraTargetDragMode) {
          if (pickResult.hit && pickResult.pickedPoint && pickResult.pickedMesh) {
            const isDome = pickResult.pickedMesh.name === 'dome' || pickResult.pickedMesh.name.includes('dome');

            if (!isDome) {
              logger.warn('Invalid placement: Can only place on the skybox.');
              return;
            }

            const worldPos = pickResult.pickedPoint;

            // Transform world position to local space when parent has rotation
            // The picked point is in world coordinates, but hotspot positions are local to parent
            const parentRotationY = this.sceneContentParent.rotation.y;
            let localPos: Vector3;

            if (parentRotationY !== 0) {
              // Create inverse rotation matrix to transform world -> local
              const inverseRotation = Matrix.RotationY(-parentRotationY);
              localPos = Vector3.TransformCoordinates(worldPos, inverseRotation);
            } else {
              localPos = worldPos;
            }

            if (this.cameraTargetDragMode) {
              // Camera target placement
              this.updateCameraTarget({ x: localPos.x, y: localPos.y, z: localPos.z });
              if (this.onCameraTargetMoved) {
                this.onCameraTargetMoved({ x: localPos.x, y: localPos.y, z: localPos.z });
              }
              this.disableCameraTargetPlacement();
            } else if (this.repositionHotspotId) {
              // Hotspot repositioning
              if (this.onHotspotRepositioned) {
                this.onHotspotRepositioned(this.repositionHotspotId, {
                  x: localPos.x,
                  y: localPos.y,
                  z: localPos.z,
                });
              }
              this.disableHotspotRepositioning();
            } else {
              // Hotspot creation
              if (this.onHotspotCreated) {
                this.onHotspotCreated({
                  x: localPos.x,
                  y: localPos.y,
                  z: localPos.z,
                });
              }
            }
          }
        }
      }

      // POINTER MOVE - Handle dragging
      if (pointerInfo.type === PointerEventTypes.POINTERMOVE) {
        if (!this.draggingMesh || !this.dragStartPointer) return;

        // Check if we've moved enough to consider it a drag
        const dx = this.scene.pointerX - this.dragStartPointer.x;
        const dy = this.scene.pointerY - this.dragStartPointer.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance > this.dragThreshold) {
          this.isDragging = true;

          // Human scale reference: drag on ground plane (XZ)
          if (this.draggingMesh.metadata?.isHumanScaleReference) {
            // Create a pick ray and find intersection with ground plane (y=0)
            const ray = this.scene.createPickingRay(
              this.scene.pointerX,
              this.scene.pointerY,
              null,
              this.camera
            );

            // Calculate intersection with y=0 plane
            if (ray.direction.y !== 0) {
              const t = -ray.origin.y / ray.direction.y;
              if (t > 0) {
                const groundX = ray.origin.x + t * ray.direction.x;
                const groundZ = ray.origin.z + t * ray.direction.z;
                // Update position - setHumanScalePosition expects feet position (y=0)
                this.modelEditorComponent.setHumanScalePosition({ x: groundX, y: 0, z: groundZ });
              }
            }
            return;
          }

          // Raycast to dome for new position
          const pickResult = this.scene.pick(this.scene.pointerX, this.scene.pointerY, (mesh) => {
            return mesh.name === 'dome' || mesh.name.includes('dome');
          });

          if (pickResult.hit && pickResult.pickedPoint) {
            const newPos = pickResult.pickedPoint;

            // Transform world position to local space when parent has rotation
            // The picked point is in world coordinates, but mesh position is local to parent
            const parentRotationY = this.sceneContentParent.rotation.y;
            let localDirection: Vector3;

            if (parentRotationY !== 0) {
              // Create inverse rotation matrix to transform world -> local
              const inverseRotation = Matrix.RotationY(-parentRotationY);
              const worldDirection = new Vector3(newPos.x, newPos.y, newPos.z).normalize();
              localDirection = Vector3.TransformCoordinates(worldDirection, inverseRotation);
            } else {
              localDirection = new Vector3(newPos.x, newPos.y, newPos.z).normalize();
            }

            // View direction target - position on dome surface at fixed radius
            if (this.draggingMesh.metadata?.isViewDirectionTarget) {
              this.draggingMesh.position = localDirection.scale(100);
            } else if (this.draggingMesh.metadata?.isCameraTarget) {
              // Camera target is positioned closer (100 units)
              this.draggingMesh.position = localDirection.scale(100);
            } else {
              const newPosition = localDirection.scale(450);

              // For floor arrows: collision plane is parented to visual mesh,
              // so moving the parent (visualMesh) moves the child automatically
              if (this.draggingMesh.metadata?.visualMesh) {
                this.draggingMesh.metadata.visualMesh.position = newPosition;
              } else {
                this.draggingMesh.position = newPosition;
              }
            }
          }
        }
      }

      // POINTER UP - End drag
      if (pointerInfo.type === PointerEventTypes.POINTERUP) {
        if (this.draggingMesh && this.isDragging) {
          // Get the actual mesh that was moved (for floor arrows, use visualMesh from metadata)
          const movedMesh = this.draggingMesh.metadata?.visualMesh || this.draggingMesh;
          const pos = movedMesh.position.clone();
          const position = { x: pos.x, y: pos.y, z: pos.z };

          // Fire appropriate callback
          if (this.draggingMesh.metadata?.isViewDirectionTarget) {
            const idx = this.draggingMesh.metadata.indicatorIndex as number;
            // Normalize a copy to get unit direction vector (scene-local)
            const dir = movedMesh.position.clone().normalize();
            if (this.onViewDirectionTargetMoved) {
              this.onViewDirectionTargetMoved(idx, { x: dir.x, y: dir.y, z: dir.z });
            }
          } else if (this.draggingMesh.metadata?.isCameraTarget) {
            if (this.onCameraTargetMoved) {
              this.onCameraTargetMoved(position);
            }
          } else if (this.draggingMesh.metadata?.isHumanScaleReference) {
            // Human scale callback is already fired via setHumanScalePosition
            // Just log the final position
            const feetPos = this.modelEditorComponent.getHumanScalePosition();
            logger.debug('Human scale drag ended at feet position:', feetPos);
          } else if (this.draggingMesh.metadata?.config) {
            const hotspotConfig = this.draggingMesh.metadata.config as HotspotConfig;
            if (this.onHotspotDragged) {
              this.onHotspotDragged(hotspotConfig.id, position);
            }
          }

          logger.debug('Drag ended at:', position);
        } else if (this.draggingMesh && !this.isDragging) {
          // Click (not drag) - navigate to editor section
          if (this.draggingMesh.metadata?.config) {
            const hotspotConfig = this.draggingMesh.metadata.config as HotspotConfig;
            if (this.onHotspotClick) {
              logger.debug('Click detected on hotspot:', hotspotConfig.id);
              this.onHotspotClick(hotspotConfig.id);
            }
          }
        }

        // Reset drag state and reattach camera controls
        if (this.draggingMesh) {
          this.camera.attachControl(this.canvas, true);
        }
        this.draggingMesh = null;
        this.isDragging = false;
        this.dragStartPointer = null;
        this.canvas.style.cursor = 'default';
      }
    });
  }

  /**
   * Enable hotspot creation mode
   */
  enableHotspotCreation(): void {
    this.hotspotCreationMode = true;
    this.repositionHotspotId = null; // Ensure mutually exclusive
    this.canvas.style.cursor = 'crosshair';
    logger.debug('Hotspot creation mode enabled');
  }

  /**
   * Disable hotspot creation mode
   */
  disableHotspotCreation(): void {
    this.hotspotCreationMode = false;
    this.canvas.style.cursor = 'default';
    logger.debug('Hotspot creation mode disabled');
  }

  /**
   * Enable hotspot repositioning mode
   */
  enableHotspotRepositioning(hotspotId: string): void {
    this.repositionHotspotId = hotspotId;
    this.hotspotCreationMode = false; // Ensure mutually exclusive
    this.canvas.style.cursor = 'move';
    logger.debug(`Repositioning mode enabled for ${hotspotId}`);
  }

  /**
   * Disable hotspot repositioning mode
   */
  disableHotspotRepositioning(): void {
    this.repositionHotspotId = null;
    this.canvas.style.cursor = 'default';
    logger.debug('Repositioning mode disabled');
  }

  // =========================================================================
  // CAMERA TARGET METHODS
  // =========================================================================

  /**
   * Create the camera target mesh (crosshair icon)
   * This shows where the camera will look when the stage loads
   */
  private createCameraTargetMesh(): Mesh {
    // Create a billboarded plane for the target icon
    // Size 25 at distance 100 - visible but not overwhelming
    const size = 25;
    const mesh = MeshBuilder.CreatePlane('cameraTarget', {
      size: size,
      sideOrientation: Mesh.DOUBLESIDE,
    }, this.scene);

    mesh.billboardMode = Mesh.BILLBOARDMODE_ALL;
    mesh.renderingGroupId = 2; // Above hotspots
    mesh.isPickable = true;

    // Create material with crosshair texture
    const material = new StandardMaterial('cameraTargetMaterial', this.scene);

    // Create dynamic texture with crosshair icon
    const textureSize = 256;
    const texture = new DynamicTexture('cameraTargetTexture', textureSize, this.scene, true);
    const ctx = texture.getContext() as CanvasRenderingContext2D;

    // Clear with transparency
    ctx.clearRect(0, 0, textureSize, textureSize);

    // Draw crosshair
    const center = textureSize / 2;
    const outerRadius = textureSize * 0.4;
    const innerRadius = textureSize * 0.15;
    const lineWidth = 12; // Thicker lines for better visibility

    ctx.strokeStyle = '#FF8C00'; // Orange color
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';

    // Outer circle
    ctx.beginPath();
    ctx.arc(center, center, outerRadius, 0, Math.PI * 2);
    ctx.stroke();

    // Inner circle
    ctx.beginPath();
    ctx.arc(center, center, innerRadius, 0, Math.PI * 2);
    ctx.stroke();

    // Cross lines (from inner circle to outer circle)
    const gap = innerRadius + 5;
    const end = outerRadius - 5;

    // Top
    ctx.beginPath();
    ctx.moveTo(center, center - gap);
    ctx.lineTo(center, center - end);
    ctx.stroke();

    // Bottom
    ctx.beginPath();
    ctx.moveTo(center, center + gap);
    ctx.lineTo(center, center + end);
    ctx.stroke();

    // Left
    ctx.beginPath();
    ctx.moveTo(center - gap, center);
    ctx.lineTo(center - end, center);
    ctx.stroke();

    // Right
    ctx.beginPath();
    ctx.moveTo(center + gap, center);
    ctx.lineTo(center + end, center);
    ctx.stroke();

    // Center dot
    ctx.fillStyle = '#FF8C00';
    ctx.beginPath();
    ctx.arc(center, center, 6, 0, Math.PI * 2);
    ctx.fill();

    texture.update();
    texture.hasAlpha = true;

    material.diffuseTexture = texture;
    material.emissiveTexture = texture;
    material.useAlphaFromDiffuseTexture = true;
    material.emissiveColor = new Color3(1, 0.55, 0); // Orange glow
    material.backFaceCulling = false;

    mesh.material = material;
    mesh.metadata = { isCameraTarget: true };

    return mesh;
  }

  /**
   * Update or create the camera target position
   * @param position - 3D position on the dome where camera should look
   */
  updateCameraTarget(position: Vector3Config | null): void {
    logger.debug('updateCameraTarget called with:', position);

    if (!position) {
      // Hide camera target if no position
      if (this.cameraTargetMesh) {
        this.cameraTargetMesh.setEnabled(false);
      }
      return;
    }

    // Create mesh if it doesn't exist
    if (!this.cameraTargetMesh) {
      logger.debug('Creating camera target mesh...');
      this.cameraTargetMesh = this.createCameraTargetMesh();
      logger.debug('Camera target mesh created:', this.cameraTargetMesh.name, 'isEnabled:', this.cameraTargetMesh.isEnabled());
    }

    // Position the camera target in the direction of the saved position
    // Place it at 50 units - inside the dome (radius ~500) but far enough to be visible
    const pos = new Vector3(position.x, position.y, position.z);
    const direction = pos.normalize();
    const targetPosition = direction.scale(50); // Closer to camera for better visibility

    this.cameraTargetMesh.position = targetPosition;
    this.cameraTargetMesh.setEnabled(true);

    logger.debug('Camera target positioned at:', targetPosition.toString(), 'isEnabled:', this.cameraTargetMesh.isEnabled(), 'isVisible:', this.cameraTargetMesh.isVisible);
  }

  /**
   * Get current camera target position
   */
  getCameraTargetPosition(): Vector3Config | null {
    if (!this.cameraTargetMesh || !this.cameraTargetMesh.isEnabled()) {
      return null;
    }

    const pos = this.cameraTargetMesh.position;
    return { x: pos.x, y: pos.y, z: pos.z };
  }

  /**
   * Point the camera to look at a specific direction
   * Used when loading a scene with a saved initial camera target
   */
  lookAtTarget(position: Vector3Config): void {
    if (!this.camera) {
      logger.warn('lookAtTarget called but no camera!');
      return;
    }

    // Use the same math as the viewer (UIManager.orientCameraToTarget) for consistency
    // Babylon.js ArcRotateCamera: alpha=0 looks at +X, alpha=PI/2 looks at +Z
    const x = position.x;
    const y = position.y;
    const z = position.z;

    // Calculate alpha (horizontal rotation around Y axis)
    // Negate both coordinates to look AT the target (not FROM it)
    const alpha = Math.atan2(-z, -x);

    // Calculate beta (vertical angle from Y axis)
    // beta = PI/2 is horizontal, < PI/2 looks down, > PI/2 looks up
    const horizontalDist = Math.sqrt(x * x + z * z);
    const beta = Math.PI / 2 + Math.atan2(y, horizontalDist);

    logger.debug('lookAtTarget called with position:', { x, y, z });
    logger.debug('Camera BEFORE - alpha:', this.camera.alpha, 'beta:', this.camera.beta);
    logger.debug('Calculated - alpha:', alpha, 'beta:', beta);

    this.camera.alpha = alpha;
    this.camera.beta = beta;

    logger.debug('Camera AFTER - alpha:', this.camera.alpha, 'beta:', this.camera.beta);
  }

  /**
   * Enable camera target drag mode
   * When enabled, clicking/dragging on the dome will move the camera target
   */
  enableCameraTargetPlacement(): void {
    this.cameraTargetDragMode = true;
    this.hotspotCreationMode = false;
    this.repositionHotspotId = null;
    this.canvas.style.cursor = 'crosshair';
    logger.debug('Camera target placement mode enabled');
  }

  /**
   * Disable camera target drag mode
   */
  disableCameraTargetPlacement(): void {
    this.cameraTargetDragMode = false;
    this.canvas.style.cursor = 'default';
    logger.debug('Camera target placement mode disabled');
  }

  /**
   * Remove camera target from scene
   */
  removeCameraTarget(): void {
    if (this.cameraTargetMesh) {
      this.cameraTargetMesh.dispose();
      this.cameraTargetMesh = null;
    }
  }

  /**
   * Add default preview light
   */
  private addDefaultLight(): void {
    const light = new HemisphericLight('previewLight', new Vector3(0, 1, 0), this.scene);
    light.intensity = 1.0;
    this.currentLights.push(light);
  }

  /**
   * Rotate a Vector3 around the Y axis by the given angle
   */
  private rotateVectorY(v: Vector3, angle: number): Vector3 {
    if (angle === 0) return v;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return new Vector3(
      v.x * cos - v.z * sin,
      v.y,
      v.x * sin + v.z * cos
    );
  }

  /**
   * Update lights in the scene
   */
  private updateLights(lightConfigs?: LightConfig[]): void {
    // Dispose existing lights (except default hemispheric if we want to keep it, but usually we replace)
    this.currentLights.forEach(light => light.dispose());
    this.currentLights = [];

    if (!lightConfigs || lightConfigs.length === 0) {
      this.addDefaultLight();
      return;
    }

    const rotationY = this.sceneContentParent.rotation.y;

    lightConfigs.forEach((config, index) => {
      let light: Light | null = null;
      const name = `light_${index}`;

      try {
        switch (config.type) {
          case 'hemispheric': {
            const dir = this.rotateVectorY(
              new Vector3(config.direction?.x || 0, config.direction?.y || 1, config.direction?.z || 0),
              rotationY
            );
            light = new HemisphericLight(name, dir, this.scene);
            if (config.groundColor) {
              (light as HemisphericLight).groundColor = Color3.FromHexString(config.groundColor);
            }
            break;
          }

          case 'directional': {
            const dir = this.rotateVectorY(
              new Vector3(config.direction?.x || 0, config.direction?.y || -1, config.direction?.z || 0),
              rotationY
            );
            light = new DirectionalLight(name, dir, this.scene);
            break;
          }

          case 'point': {
            const pos = this.rotateVectorY(
              new Vector3(config.position?.x || 0, config.position?.y || 0, config.position?.z || 0),
              rotationY
            );
            light = new PointLight(name, pos, this.scene);
            break;
          }

          case 'spot': {
            const pos = this.rotateVectorY(
              new Vector3(config.position?.x || 0, config.position?.y || 0, config.position?.z || 0),
              rotationY
            );
            const dir = this.rotateVectorY(
              new Vector3(config.direction?.x || 0, config.direction?.y || -1, config.direction?.z || 0),
              rotationY
            );
            light = new SpotLight(name, pos, dir, config.angle || Math.PI / 3, config.exponent || 2, this.scene);
            break;
          }
        }

        if (light) {
          light.intensity = config.intensity !== undefined ? config.intensity : 1;
          if (config.diffuse) {
            light.diffuse = Color3.FromHexString(config.diffuse);
          }
          if (config.specular) {
            light.specular = Color3.FromHexString(config.specular);
          }
          this.currentLights.push(light);
        }
      } catch (e) {
        logger.error(`Failed to create light ${index}:`, e);
      }
    });
  }
  startRendering(): void {
    if (this.isRendering) return;

    this.isRendering = true;
    this.engine.runRenderLoop(() => {
      this.scene.render();
    });

    // Force multiple resizes to ensure correct viewport
    // Some browsers need time to properly layout the canvas
    this.engine.resize();

    // Delayed resize to catch late layout updates
    setTimeout(() => {
      this.engine.resize();
      logger.debug('Delayed resize completed');
    }, 100);

    setTimeout(() => {
      this.engine.resize();
      // Mark as fully initialized after all resizes complete
      this.isInitialized = true;
      logger.debug('Fully initialized');
    }, 300);

    logger.debug('Rendering started');
  }

  /**
   * 🔍 DIAGNOSTIC: Comprehensive state dump for debugging skybox issues
   */
  public diagnoseSkyboxState(context: string = 'unknown'): void {
    logger.group(`🔍 [DIAGNOSTIC] Skybox State @ ${context}`);

    // 1. Engine State
    logger.group('1️⃣ Engine State');
    logger.debug('isRendering:', this.isRendering);
    logger.debug('isInitialized:', this.isInitialized);
    logger.debug('canvas dimensions:', this.canvas.width, 'x', this.canvas.height);
    logger.debug('canvas clientDimensions:', this.canvas.clientWidth, 'x', this.canvas.clientHeight);
    logger.debug('engine getRenderWidth:', this.engine.getRenderWidth());
    logger.debug('engine getRenderHeight:', this.engine.getRenderHeight());
    // @ts-ignore - accessing internal for debug
    logger.debug('WebGL context lost:', this.engine._gl?.isContextLost?.() ?? 'unknown');
    logger.groupEnd();

    // 2. Scene State
    logger.group('2️⃣ Scene State');
    logger.debug('scene isReady:', this.scene.isReady());
    logger.debug('total meshes:', this.scene.meshes.length);
    logger.debug('active meshes:', this.scene.getActiveMeshes().length);
    logger.debug('mesh names:', this.scene.meshes.map(m => m.name).join(', '));
    logger.debug('total materials:', this.scene.materials.length);
    logger.debug('total textures:', this.scene.textures.length);
    logger.debug('clearColor:', this.scene.clearColor?.toString());
    logger.groupEnd();

    // 3. Camera State
    logger.group('3️⃣ Camera State');
    logger.debug('camera position:', this.camera.position.toString());
    logger.debug('camera target:', this.camera.target.toString());
    logger.debug('camera alpha:', this.camera.alpha);
    logger.debug('camera beta:', this.camera.beta);
    logger.debug('camera radius:', this.camera.radius);
    logger.debug('camera minZ:', this.camera.minZ);
    logger.debug('camera maxZ:', this.camera.maxZ);
    logger.debug('camera fov:', this.camera.fov);
    logger.groupEnd();

    // 4. Dome Mesh State
    logger.group('4️⃣ Dome Mesh State');
    const dome = this.scene.getMeshByName('dome');
    if (dome) {
      logger.debug('✅ Dome mesh FOUND');
      logger.debug('isVisible:', dome.isVisible);
      logger.debug('isEnabled:', dome.isEnabled());
      logger.debug('isPickable:', dome.isPickable);
      logger.debug('alwaysSelectAsActiveMesh:', dome.alwaysSelectAsActiveMesh);
      logger.debug('infiniteDistance:', dome.infiniteDistance);
      logger.debug('renderingGroupId:', dome.renderingGroupId);
      logger.debug('position:', dome.position.toString());
      logger.debug('rotation:', dome.rotation.toString());
      logger.debug('scaling:', dome.scaling.toString());
      logger.debug('absolutePosition:', dome.absolutePosition.toString());
      logger.debug('getBoundingInfo center:', dome.getBoundingInfo()?.boundingBox?.centerWorld?.toString());
      // Only check frustum if planes are available (scene must have rendered at least once)
      try {
        if (this.scene.frustumPlanes && this.scene.frustumPlanes.length > 0) {
          logger.debug('isInFrustum:', dome.isInFrustum(this.scene.frustumPlanes));
        } else {
          logger.debug('isInFrustum: N/A (frustum planes not ready yet)');
        }
      } catch (e) {
        logger.debug('isInFrustum: ERROR -', (e as Error).message);
      }
      logger.debug('material:', dome.material?.name || 'NONE');
      logger.debug('uniqueId:', dome.uniqueId);

      // Check if dome is in active meshes
      const activeMeshes = this.scene.getActiveMeshes();
      const isActive = activeMeshes.data.some((m: any) => m?.uniqueId === dome.uniqueId);
      logger.debug('isInActiveMeshes:', isActive);
    } else {
      logger.debug('❌ Dome mesh NOT FOUND!');
      logger.debug('All mesh names:', this.scene.meshes.map(m => `${m.name}(${m.uniqueId})`).join(', '));
    }
    logger.groupEnd();

    // 5. Dome Material State
    logger.group('5️⃣ Dome Material State');
    const domeMaterial = this.scene.getMaterialByName('domeMaterial');
    if (domeMaterial) {
      logger.debug('✅ Dome material FOUND');
      // @ts-ignore - accessing PBR properties
      const pbrMat = domeMaterial as any;
      logger.debug('unlit:', pbrMat.unlit);
      logger.debug('backFaceCulling:', pbrMat.backFaceCulling);
      logger.debug('transparencyMode:', pbrMat.transparencyMode);
      logger.debug('albedoColor:', pbrMat.albedoColor?.toString());
      logger.debug('albedoTexture:', pbrMat.albedoTexture ? 'EXISTS' : 'NULL');

      if (pbrMat.albedoTexture) {
        const tex = pbrMat.albedoTexture;
        logger.debug('  texture name:', tex.name);
        logger.debug('  texture isReady:', tex.isReady());
        logger.debug('  texture getSize:', JSON.stringify(tex.getSize()));
        logger.debug('  texture hasAlpha:', tex.hasAlpha);
        logger.debug('  texture url:', tex.url?.substring(0, 100) || 'N/A');
        // @ts-ignore
        logger.debug('  texture _texture exists:', !!tex._texture);
      }
    } else {
      logger.debug('❌ Dome material NOT FOUND!');
    }
    logger.groupEnd();

    // 6. Load Generation State
    logger.group('6️⃣ Load State');
    logger.debug('skyboxLoadGeneration:', this.skyboxLoadGeneration);
    logger.groupEnd();

    logger.groupEnd(); // End main group
  }

  /**
   * Check if preview is fully initialized and ready
   */
  isReady(): boolean {
    return this.isInitialized && this.isRendering;
  }

  /**
   * Stop rendering loop
   */
  stopRendering(): void {
    if (!this.isRendering) return;

    this.isRendering = false;
    this.engine.stopRenderLoop();

    logger.debug('Rendering stopped');
  }

  /**
   * Update preview with stage configuration - DIRECT, NO DEBOUNCE
   * @param stage - The stage configuration to load
   * @param stageIndex - The index of this stage (0 = first stage)
   * @returns Promise that resolves when the stage is fully loaded
   */
  async updateStage(stage: ExperienceStage, stageIndex: number = 0): Promise<void> {
    logger.debug('updateStage - loading immediately:', stage?.id || 'unknown', 'index:', stageIndex);

    // Store stage index for camera target visibility
    this.currentStageIndex = stageIndex;

    // Show loading and load directly - no debounce nonsense
    this.setLoading(true);
    await this.loadStage(stage);
  }

  /**
   * Set loading state with automatic timeout safety
   */
  public setLoading(isLoading: boolean): void {
    // Clear any existing loading timeout
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }

    const overlay = document.getElementById('previewLoadingOverlay');
    if (overlay) {
      if (isLoading) {
        overlay.classList.add('visible');

        // SAFETY: Auto-hide loading after 10 seconds max to prevent stuck state
        this.loadingTimeout = setTimeout(() => {
          logger.warn('Loading timeout - forcing hide');
          overlay.classList.remove('visible');
          this.loadingTimeout = null;
        }, 10000);
      } else {
        overlay.classList.remove('visible');
      }
    }
  }

  /**
   * Force clear loading state (emergency use)
   */
  public forceHideLoading(): void {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
    const overlay = document.getElementById('previewLoadingOverlay');
    if (overlay) {
      overlay.classList.remove('visible');
    }
  }

  /**
   * Helper: Promise with timeout
   */
  private withTimeout<T>(promise: Promise<T>, ms: number, name: string): Promise<T> {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(() => reject(new Error(`${name} timed out after ${ms}ms`)), ms)
      )
    ]);
  }

  /**
   * Load a stage into the preview
   */
  private async loadStage(stage: ExperienceStage): Promise<void> {
    logger.debug('═══════════════════════════════════════════════════');
    logger.debug('loadStage START for:', stage?.id || 'unknown');
    logger.debug('Stage skybox config:', JSON.stringify(stage?.skybox, null, 2));

    // Run initial diagnostic
    this.diagnoseSkyboxState('loadStage START');

    // Loading indicator is already shown by updateStage for instant feedback
    // But ensure it's visible in case loadStage is called directly
    this.setLoading(true);

    try {
      // Reset parent rotation (will be set by updateSkybox if rotation is configured)
      this.sceneContentParent.rotation.y = 0;

      // Clear previous content
      logger.debug('Clearing lights...');
      this.clearLights();

      // Load skybox (with 8 second timeout)
      if (stage.skybox) {
        logger.debug('🎯 Loading skybox - type:', stage.skybox.type);
        try {
          await this.withTimeout(this.updateSkybox(stage.skybox), 8000, 'Skybox load');
          logger.debug('✅ Skybox load completed');

          // CRITICAL: Force scene to re-evaluate active meshes after skybox load
          // This ensures the dome is included in the render list
          this.scene.freeActiveMeshes();

          // Run diagnostic AFTER skybox load
          this.diagnoseSkyboxState('AFTER skybox load');

        } catch (skyboxErr) {
          logger.error('❌ Skybox load FAILED:', skyboxErr);
          this.diagnoseSkyboxState('AFTER skybox FAILURE');
          // Continue loading other elements even if skybox fails
        }
      } else {
        logger.debug('⚠️ No skybox config - unloading dome');
        this.domeComponent.unload();
      }

      // Load all models (uses the proper loadModels method)
      if (stage.models && stage.models.length > 0) {
        logger.debug(`Loading ${stage.models.length} model(s)...`);
        try {
          await this.withTimeout(this.loadModels(stage.models), 8000, 'Models load');
        } catch (modelErr) {
          logger.error('Models load failed:', modelErr);
        }
      } else {
        // No models - just clear any existing
        this.modelComponent.unload();
      }

      // Load planes (with 5 second timeout)
      this.planeComponent.unload();
      if (stage.planes && stage.planes.length > 0) {
        logger.debug('Loading planes...');
        try {
          await this.withTimeout(this.planeComponent.load(stage.planes), 5000, 'Planes load');
          logger.debug('Planes loaded');
        } catch (planeErr) {
          logger.error('Planes load failed:', planeErr);
        }
      }

      // Load lights (synchronous)
      logger.debug('Updating lights...');
      this.updateLights(stage.lights);

      // Load hotspots (with 5 second timeout)
      // Pass enableInteraction = false so hotspots are visible but non-interactive
      // This shows users exactly what viewers will see, without blocking camera controls
      this.floorArrowManager.dispose(); // Clear existing floor arrows
      if (stage.hotspots && stage.hotspots.length > 0) {
        logger.debug('Loading hotspots (visual only, no interaction)...');
        try {
          await this.withTimeout(this.hotspotComponent.load(stage.hotspots, true, false), 5000, 'Hotspots load');
          logger.debug('Hotspots loaded');

          // Create floor arrows for floor-arrow style hotspots
          logger.debug('Checking hotspots for floor arrows:', stage.hotspots.map((h: HotspotConfig) => ({
            id: h.id,
            type: h.type,
            portalEffectStyle: h.portalEffectStyle,
            targetSceneId: h.targetSceneId || h.targetStageId
          })));
          // Show floor arrows even without destination (in editor, user may not have set destination yet)
          const floorArrowHotspots = stage.hotspots.filter(
            (h: HotspotConfig) => h.type === 'navigation' && h.portalEffectStyle === 'floor-arrow'
          );
          logger.debug(`Found ${floorArrowHotspots.length} floor-arrow hotspots`);
          for (const hotspot of floorArrowHotspots) {
            logger.debug(`Creating floor arrow for ${hotspot.id}, direction: ${hotspot.arrowDirection}`);
            this.floorArrowManager.createArrow(hotspot);
          }
        } catch (hotspotErr) {
          logger.error('Hotspots load failed:', hotspotErr);
        }
      }

      // Load camera target - ONLY for first stage (index 0)
      // Other stages use portal's initialViewDirection for camera orientation
      if (this.currentStageIndex === 0) {
        if (stage.initialCameraTarget) {
          logger.debug('Loading camera target from config (first stage):', stage.initialCameraTarget);
          this.updateCameraTarget(stage.initialCameraTarget);
          // Also point the camera to look at the target
          this.lookAtTarget(stage.initialCameraTarget);
        } else {
          // No camera target configured - show at default forward position
          logger.debug('No camera target configured, showing default (first stage)...');
          this.updateCameraTarget({ x: 0, y: 0, z: 100 }); // Default: looking forward
          this.lookAtTarget({ x: 0, y: 0, z: 100 });
        }
      } else {
        // Hide camera target for non-first stages
        logger.debug('Hiding camera target (not first stage)');
        this.updateCameraTarget(null);
      }

      logger.debug('✅ Stage loaded successfully');

      // Final diagnostic after everything is loaded
      this.diagnoseSkyboxState('loadStage COMPLETE');
      logger.debug('═══════════════════════════════════════════════════');
    } catch (error) {
      logger.error('❌ Failed to update stage:', error);
      this.diagnoseSkyboxState('loadStage ERROR');
    } finally {
      // ALWAYS hide loading indicator
      logger.debug('loadStage END - hiding loading');
      this.setLoading(false);
    }
  }

  /**
   * Update skybox based on config
   */
  private async updateSkybox(config: SkyboxConfig): Promise<void> {
    // Increment generation to invalidate any in-flight loads from previous calls
    this.skyboxLoadGeneration++;
    const thisGeneration = this.skyboxLoadGeneration;

    logger.debug(`updateSkybox called (gen ${thisGeneration}):`, {
      type: config.type,
      hasUrl: !!config.url,
      hasVariants: !!config.variants,
      hasColor: !!config.color,
      url: config.url,
      desktopUrl: config.variants?.desktop?.url,
      fallbackUrl: config.variants?.fallback?.url
    });

    // Check if we have any valid skybox data (URL or variants or color)
    const hasUrl = config.url || config.variants?.desktop?.url || config.variants?.fallback?.url;
    if (!hasUrl && !config.color) {
      logger.debug('No valid skybox URL or color, unloading dome');
      this.domeComponent.unload();
      return;
    }

    // Helper to validate URLs (must start with http/https, not be an email, etc.)
    const isValidUrl = (url: string | undefined): boolean => {
      if (!url) return false;
      // Must start with http:// or https://
      if (!url.match(/^https?:\/\//i)) {
        logger.warn(`⚠️ Invalid URL (not http/https): "${url.substring(0, 50)}..."`);
        return false;
      }
      // Reject obvious non-URLs (like email addresses that somehow got in)
      if (url.includes('@') && !url.includes('token=')) {
        logger.warn(`⚠️ Invalid URL (looks like email): "${url.substring(0, 50)}..."`);
        return false;
      }
      return true;
    };

    try {
      if (config.type === 'image' && hasUrl) {
        // Editor preview: Try desktop variant first, fallback to JPG if KTX2 fails
        // (KTX2 transcoder may not be initialized in editor's separate engine)
        const desktopUrl = config.variants?.desktop?.url;
        const fallbackUrl = config.variants?.fallback?.url;
        const legacyUrl = config.url;
        const isDesktopKtx2 = desktopUrl?.match(/\.ktx2$/i);

        // 🔍 Validate all URLs upfront
        logger.debug(`🔍 URL validation:`, {
          desktopUrl: desktopUrl ? (isValidUrl(desktopUrl) ? '✅ valid' : '❌ INVALID') : 'N/A',
          fallbackUrl: fallbackUrl ? (isValidUrl(fallbackUrl) ? '✅ valid' : '❌ INVALID') : 'N/A',
          legacyUrl: legacyUrl ? (isValidUrl(legacyUrl) ? '✅ valid' : '❌ INVALID') : 'N/A'
        });

        let texture: Awaited<ReturnType<typeof this.domeComponent.loadInBackground>> | null = null;
        let loadedUrl: string | null = null;

        // Try desktop variant first (may be KTX2) - ONLY if URL is valid
        if (desktopUrl && isValidUrl(desktopUrl)) {
          logger.debug(`(gen ${thisGeneration}) Trying desktop variant: ${desktopUrl.substring(0, 80)}... (KTX2: ${!!isDesktopKtx2})`);
          try {
            const mediaItem: MediaItem = {
              id: 'skybox',
              title: 'Skybox',
              url: desktopUrl,
              type: 'image'
            };
            texture = await this.domeComponent.loadInBackground(mediaItem, 'image');

            // Check if this load is still relevant
            if (thisGeneration !== this.skyboxLoadGeneration) {
              logger.debug(`⏭️ Discarding stale load (gen ${thisGeneration}, current ${this.skyboxLoadGeneration})`);
              texture.dispose();
              return;
            }

            loadedUrl = desktopUrl;
            logger.debug(`✅ (gen ${thisGeneration}) Desktop variant loaded successfully`);
          } catch (desktopErr) {
            logger.warn(`(gen ${thisGeneration}) Desktop variant failed:`, desktopErr);
          }
        } else if (desktopUrl) {
          logger.debug(`⏭️ Skipping invalid desktop URL`);
        }

        // Check generation before continuing to fallback
        if (thisGeneration !== this.skyboxLoadGeneration) {
          logger.debug(`⏭️ Aborting stale skybox load (gen ${thisGeneration})`);
          return;
        }

        // Fallback to JPG variant if desktop failed (or was KTX2 that failed) - ONLY if URL is valid
        if (!texture && fallbackUrl && isValidUrl(fallbackUrl)) {
          logger.debug(`(gen ${thisGeneration}) Trying fallback variant (JPG): ${fallbackUrl.substring(0, 80)}...`);
          try {
            const mediaItem: MediaItem = {
              id: 'skybox',
              title: 'Skybox',
              url: fallbackUrl,
              type: 'image'
            };
            texture = await this.domeComponent.loadInBackground(mediaItem, 'image');

            // Check if this load is still relevant
            if (thisGeneration !== this.skyboxLoadGeneration) {
              logger.debug(`⏭️ Discarding stale load (gen ${thisGeneration}, current ${this.skyboxLoadGeneration})`);
              texture.dispose();
              return;
            }

            loadedUrl = fallbackUrl;
            logger.debug(`✅ (gen ${thisGeneration}) Fallback variant loaded successfully`);
          } catch (fallbackErr) {
            logger.warn(`(gen ${thisGeneration}) Fallback variant failed:`, fallbackErr);
          }
        } else if (fallbackUrl && !isValidUrl(fallbackUrl)) {
          logger.debug(`⏭️ Skipping invalid fallback URL`);
        }

        // Check generation before continuing to legacy
        if (thisGeneration !== this.skyboxLoadGeneration) {
          logger.debug(`⏭️ Aborting stale skybox load (gen ${thisGeneration})`);
          return;
        }

        // Last resort: try legacy URL - ONLY if URL is valid
        if (!texture && legacyUrl && isValidUrl(legacyUrl)) {
          logger.debug(`(gen ${thisGeneration}) Trying legacy URL: ${legacyUrl.substring(0, 80)}...`);
          try {
            const mediaItem: MediaItem = {
              id: 'skybox',
              title: 'Skybox',
              url: legacyUrl,
              type: 'image'
            };
            texture = await this.domeComponent.loadInBackground(mediaItem, 'image');

            // Check if this load is still relevant
            if (thisGeneration !== this.skyboxLoadGeneration) {
              logger.debug(`⏭️ Discarding stale load (gen ${thisGeneration}, current ${this.skyboxLoadGeneration})`);
              texture.dispose();
              return;
            }

            loadedUrl = legacyUrl;
            logger.debug(`✅ (gen ${thisGeneration}) Legacy URL loaded successfully`);
          } catch (legacyErr) {
            logger.warn(`(gen ${thisGeneration}) Legacy URL failed:`, legacyErr);
          }
        }

        // Final generation check before applying texture
        if (thisGeneration !== this.skyboxLoadGeneration) {
          logger.debug(`⏭️ Aborting stale skybox load before swap (gen ${thisGeneration})`);
          if (texture) texture.dispose();
          return;
        }

        // Apply texture if we got one
        if (texture && loadedUrl) {
          // 🔍 PRE-SWAP DIAGNOSTIC
          logger.debug(`🔄 (gen ${thisGeneration}) PRE-SWAP texture state:`, {
            textureExists: !!texture,
            textureIsReady: texture.isReady(),
            textureSize: JSON.stringify(texture.getSize()),
            textureName: texture.name,
            // @ts-ignore
            textureInternalExists: !!texture._texture,
            loadedUrl: loadedUrl.substring(0, 80) + '...'
          });

          const mediaItem: MediaItem = {
            id: 'skybox',
            title: 'Skybox',
            url: loadedUrl,
            type: 'image'
          };

          // Perform the swap
          this.domeComponent.swapTexture(texture, mediaItem, 'image');
          logger.debug(`✅ (gen ${thisGeneration}) Skybox texture swapped`);

          // 🔍 POST-SWAP DIAGNOSTIC
          const dome = this.scene.getMeshByName('dome');
          const domeMat = this.scene.getMaterialByName('domeMaterial') as any;
          logger.debug(`🔄 (gen ${thisGeneration}) POST-SWAP state:`, {
            domeExists: !!dome,
            domeVisible: dome?.isVisible,
            domeEnabled: dome?.isEnabled(),
            materialExists: !!domeMat,
            albedoTextureExists: !!domeMat?.albedoTexture,
            albedoTextureReady: domeMat?.albedoTexture?.isReady(),
            albedoTextureSize: domeMat?.albedoTexture ? JSON.stringify(domeMat.albedoTexture.getSize()) : 'N/A',
            albedoColor: domeMat?.albedoColor?.toString()
          });

          // Apply rotation to parent node (not dome directly)
          if (config.rotation !== undefined) {
            const radians = (config.rotation * Math.PI) / 180;
            this.sceneContentParent.rotation.y = radians;
            this.domeComponent.resetRotation(); // Keep dome at local Math.PI
            logger.debug(`Applied rotation to parent: ${config.rotation}° (${radians} radians)`);
          } else {
            this.sceneContentParent.rotation.y = 0;
            this.domeComponent.resetRotation();
          }
        } else {
          logger.error(`❌ (gen ${thisGeneration}) All skybox load attempts failed!`);
          logger.error(`Texture: ${texture}, LoadedUrl: ${loadedUrl}`);
        }

      } else if (config.type === 'video' && config.url) {
        const mediaItem: MediaItem = {
          id: 'skybox',
          title: 'Skybox',
          url: config.url,
          thumbnailUrl: config.url,
          type: 'video'
        };
        logger.debug(`(gen ${thisGeneration}) Loading video skybox...`);
        const texture = await this.domeComponent.loadInBackground(mediaItem, 'video');

        // Check if this load is still relevant
        if (thisGeneration !== this.skyboxLoadGeneration) {
          logger.debug(`⏭️ Discarding stale video load (gen ${thisGeneration}, current ${this.skyboxLoadGeneration})`);
          texture.dispose();
          return;
        }

        this.domeComponent.swapTexture(texture, mediaItem, 'video');
        logger.debug(`✅ (gen ${thisGeneration}) Video skybox loaded`);

        // Apply video settings
        if (config.autoplay) {
          await this.domeComponent.play();
        }

      } else if (config.type === 'color' && config.color) {
        logger.debug('Setting solid color:', config.color);
        this.domeComponent.setSolidColor(config.color);
      } else {
        // No matching condition - log what we got
        logger.warn('Skybox config did not match any load condition:', {
          type: config.type,
          hasUrl,
          hasColor: !!config.color
        });
      }

      // Apply rotation to parent node (for video/color types that didn't set it above)
      if (config.rotation !== undefined) {
        const radians = (config.rotation * Math.PI) / 180;
        this.sceneContentParent.rotation.y = radians;
        this.domeComponent.resetRotation();
      }

      logger.debug('updateSkybox completed successfully');
    } catch (error) {
      logger.error('Failed to load skybox:', error);
    }
  }

  /**
   * Load models - loads ALL models in the config (not just first)
   * Uses generation counter to prevent race condition duplicates
   * Loads with skipStore, then checks generation, then either stores or disposes
   */
  private async loadModels(modelConfigs: ModelConfig[]): Promise<void> {
    // Increment generation to invalidate any in-flight loads
    this.modelLoadGeneration++;
    const thisGeneration = this.modelLoadGeneration;

    logger.debug(`loadModels starting (gen ${thisGeneration}), ${modelConfigs.length} model(s)`);

    // Clear any existing models first
    this.modelComponent.unload();

    // Load all models
    for (let i = 0; i < modelConfigs.length; i++) {
      // Check if this load is still relevant before each model
      if (thisGeneration !== this.modelLoadGeneration) {
        logger.debug(`⏭️ Aborting stale model load before starting (gen ${thisGeneration}, current ${this.modelLoadGeneration})`);
        return;
      }

      const config = modelConfigs[i];
      if (!config.url) continue;

      const mediaItem: MediaItem = {
        id: `preview-model-${i}`,
        title: `Preview Model ${i + 1}`,
        url: config.url,
      };

      try {
        // Load with skipStore and skipCentering - we'll store manually after generation check
        const loadResult = await this.modelComponent.load(mediaItem, undefined, {
          skipCentering: true,
          skipStore: true
        });

        // Check generation again after async load completes
        if (thisGeneration !== this.modelLoadGeneration) {
          logger.debug(`⏭️ Discarding stale model load result (gen ${thisGeneration}, current ${this.modelLoadGeneration})`);
          // Dispose the loaded meshes since this load is stale
          if (loadResult) {
            this.modelComponent.disposeLoadedModel(loadResult.root, loadResult.meshes);
          }
          return;
        }

        // Generation still valid - store the model and apply transforms
        if (loadResult) {
          this.modelComponent.storeLoadedModel(config.url, loadResult.root, loadResult.meshes, mediaItem);

          // Apply transformations from config (or defaults)
          const rootMesh = loadResult.root;

          // Position: config values or origin
          rootMesh.position.set(
            config.position?.x || 0,
            config.position?.y || 0,
            config.position?.z || 0
          );

          // Rotation: config values or none
          rootMesh.rotation.set(
            config.rotation?.x || 0,
            config.rotation?.y || 0,
            config.rotation?.z || 0
          );

          // Scale: config values or default 1
          if (config.scale !== undefined) {
            if (typeof config.scale === 'number') {
              rootMesh.scaling.setAll(config.scale);
            } else {
              rootMesh.scaling.set(
                config.scale.x || 1,
                config.scale.y || 1,
                config.scale.z || 1
              );
            }
          } else {
            rootMesh.scaling.setAll(1);
          }
        }
      } catch (error) {
        logger.error(`Failed to load model ${i}:`, error);
        // Continue loading other models even if one fails
      }
    }

    // Final generation check
    if (thisGeneration === this.modelLoadGeneration) {
      logger.debug(`✅ Loaded ${modelConfigs.length} model(s) (gen ${thisGeneration})`);
    }
  }

  /**
   * Load lights from configuration
   */
  private loadLights(lightConfigs: LightConfig[]): void {
    lightConfigs.forEach((config, index) => {
      const light = this.createLight(config, index);
      if (light) {
        this.currentLights.push(light);
      }
    });

    logger.debug(`Loaded ${this.currentLights.length} light(s)`);
  }

  /**
   * Create a single light from configuration
   */
  private createLight(config: LightConfig, index: number): Light | null {
    let light: Light | null = null;
    const name = `preview_light_${index}`;

    try {
      switch (config.type) {
        case 'hemispheric':
          {
            const direction = config.direction
              ? new Vector3(config.direction.x, config.direction.y, config.direction.z)
              : new Vector3(0, 1, 0);

            const hemiLight = new HemisphericLight(name, direction, this.scene);
            hemiLight.intensity = config.intensity ?? 1.0;

            if (config.diffuse) {
              hemiLight.diffuse = Color3.FromHexString(config.diffuse);
            }
            if (config.specular) {
              hemiLight.specular = Color3.FromHexString(config.specular);
            }
            if (config.groundColor) {
              hemiLight.groundColor = Color3.FromHexString(config.groundColor);
            }

            light = hemiLight;
          }
          break;

        case 'directional':
          {
            const direction = config.direction
              ? new Vector3(config.direction.x, config.direction.y, config.direction.z)
              : new Vector3(-1, -2, -1);

            const dirLight = new DirectionalLight(name, direction, this.scene);
            dirLight.intensity = config.intensity ?? 1.0;

            if (config.diffuse) {
              dirLight.diffuse = Color3.FromHexString(config.diffuse);
            }
            if (config.specular) {
              dirLight.specular = Color3.FromHexString(config.specular);
            }

            light = dirLight;
          }
          break;

        case 'point':
          {
            const position = config.position
              ? new Vector3(config.position.x, config.position.y, config.position.z)
              : new Vector3(0, 3, 0);

            const pointLight = new PointLight(name, position, this.scene);
            pointLight.intensity = config.intensity ?? 1.5;

            if (config.diffuse) {
              pointLight.diffuse = Color3.FromHexString(config.diffuse);
            }
            if (config.specular) {
              pointLight.specular = Color3.FromHexString(config.specular);
            }
            if (config.range) {
              pointLight.range = config.range;
            }

            light = pointLight;
          }
          break;

        case 'spot':
          {
            const position = config.position
              ? new Vector3(config.position.x, config.position.y, config.position.z)
              : new Vector3(0, 5, 0);

            const direction = config.direction
              ? new Vector3(config.direction.x, config.direction.y, config.direction.z)
              : new Vector3(0, -1, 0);

            const spotLight = new SpotLight(
              name,
              position,
              direction,
              config.angle ?? 0.8,
              config.exponent ?? 2,
              this.scene
            );

            spotLight.intensity = config.intensity ?? 2.0;

            if (config.diffuse) {
              spotLight.diffuse = Color3.FromHexString(config.diffuse);
            }
            if (config.specular) {
              spotLight.specular = Color3.FromHexString(config.specular);
            }

            light = spotLight;
          }
          break;

        default:
          logger.error(`Unknown light type: ${config.type}`);
      }

      return light;
    } catch (error) {
      logger.error(`Failed to create light ${name}:`, error);
      return null;
    }
  }

  /**
   * Clear all lights
   */
  private clearLights(): void {
    this.currentLights.forEach((light) => {
      light.dispose();
    });
    this.currentLights = [];
  }

  /**
   * Reset camera to default position
   */
  resetCamera(): void {
    this.camera.alpha = 0;
    this.camera.beta = Math.PI / 2;
    this.camera.radius = 5;
    this.camera.target = Vector3.Zero();
    logger.debug('Camera reset');
  }

  /**
   * Set camera field of view
   * @param fovRadians FOV in radians
   */
  setFOV(fovRadians: number): void {
    this.camera.fov = fovRadians;
    logger.debug('FOV set to', fovRadians, 'radians');
  }

  /**
   * Resize preview (call when canvas size changes)
   */
  resize(): void {
    this.engine.resize();
  }

  /**
   * Get the canvas element for thumbnail capture
   */
  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  /**
   * Get the scene for debugging
   */
  getScene(): Scene {
    return this.scene;
  }

  /**
   * Change to a different canvas (for floating panels)
   */
  changeCanvas(canvasId: string): void {
    const newCanvas = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!newCanvas) {
      logger.error(`Canvas ${canvasId} not found`);
      return;
    }

    // Detach from old canvas
    this.camera.detachControl();

    // Switch to new canvas
    this.canvas = newCanvas;
    this.camera.attachControl(this.canvas, true);

    // Resize engine to match new canvas
    this.engine.resize();

    logger.debug(`Switched to canvas: ${canvasId}`);
  }

  /**
   * Setup FOV zoom controls (scroll wheel and pinch)
   * Matches the viewer behavior for consistent preview experience
   * MEMORY SAFE: Stores handler references for proper cleanup
   */
  private setupFOVZoom(): void {
    const MIN_FOV = 0.3;   // Zoomed in (narrow field of view)
    const MAX_FOV = 2.5;   // Zoomed out (ultra-wide field of view)

    // Mobile: Pinch zoom state (needs to persist across handlers)
    let lastDistance = 0;
    let isPinching = false;

    // Desktop: Mouse wheel zoom
    this.wheelHandler = (event: WheelEvent) => {
      event.preventDefault();

      const delta = Math.sign(event.deltaY);
      const zoomSpeed = 0.01875; // Reduced to 37.5% of original

      // Update FOV with clamping
      const newFOV = Math.max(MIN_FOV, Math.min(MAX_FOV,
        this.camera.fov + delta * zoomSpeed
      ));
      this.camera.fov = newFOV;
    };
    this.canvas.addEventListener('wheel', this.wheelHandler, { passive: false });

    // Mobile: Pinch zoom - touchstart
    this.touchStartHandler = (event: TouchEvent) => {
      if (event.touches.length === 2) {
        isPinching = true;
        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        lastDistance = this.getTouchDistance(touch1, touch2);
      }
    };
    this.canvas.addEventListener('touchstart', this.touchStartHandler);

    // Mobile: Pinch zoom - touchmove
    this.touchMoveHandler = (event: TouchEvent) => {
      if (isPinching && event.touches.length === 2) {
        event.preventDefault();

        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        const currentDistance = this.getTouchDistance(touch1, touch2);

        if (lastDistance > 0) {
          const delta = lastDistance - currentDistance;
          const zoomSpeed = 0.001875; // Reduced to 37.5% of original

          // Update FOV with clamping
          const newFOV = Math.max(MIN_FOV, Math.min(MAX_FOV,
            this.camera.fov + delta * zoomSpeed
          ));
          this.camera.fov = newFOV;
        }

        lastDistance = currentDistance;
      }
    };
    this.canvas.addEventListener('touchmove', this.touchMoveHandler, { passive: false });

    // Mobile: Pinch zoom - touchend
    this.touchEndHandler = (event: TouchEvent) => {
      if (event.touches.length < 2) {
        isPinching = false;
        lastDistance = 0;
      }
    };
    this.canvas.addEventListener('touchend', this.touchEndHandler);
  }

  /**
   * Calculate distance between two touch points
   */
  private getTouchDistance(touch1: Touch, touch2: Touch): number {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Dispose preview and cleanup resources
   * MEMORY SAFE: Removes all event listeners to prevent leaks
   */
  dispose(): void {
    this.stopRendering();

    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }

    // Clean up FOV zoom event listeners
    if (this.wheelHandler) {
      this.canvas.removeEventListener('wheel', this.wheelHandler);
      this.wheelHandler = null;
    }
    if (this.touchStartHandler) {
      this.canvas.removeEventListener('touchstart', this.touchStartHandler);
      this.touchStartHandler = null;
    }
    if (this.touchMoveHandler) {
      this.canvas.removeEventListener('touchmove', this.touchMoveHandler);
      this.touchMoveHandler = null;
    }
    if (this.touchEndHandler) {
      this.canvas.removeEventListener('touchend', this.touchEndHandler);
      this.touchEndHandler = null;
    }

    this.clearLights();
    this.removeCameraTarget();
    this.domeComponent.dispose();
    this.modelComponent.dispose();
    this.planeComponent.dispose();
    this.modelEditorComponent.dispose();

    // Dispose post-processing manager
    if (this.postProcessingManager) {
      this.postProcessingManager.dispose();
      this.postProcessingManager = null;
    }

    this.scene.dispose();
    this.engine.dispose();

    logger.debug('Disposed');
  }

  // =========================================================================
  // MODEL EDITOR METHODS
  // =========================================================================

  /**
   * Enable transform gizmos with the specified mode
   * @param mode - 'position' | 'rotation' | 'scale' | 'boundingBox' | 'none'
   */
  enableGizmos(mode: 'position' | 'rotation' | 'scale' | 'boundingBox' | 'none'): void {
    this.modelEditorComponent.enableGizmos(mode);
  }

  /**
   * Disable all gizmos
   */
  disableGizmos(): void {
    this.modelEditorComponent.disableGizmos();
  }

  /**
   * Attach gizmo to a model's root TransformNode by URL
   * Using individual gizmos (not GizmoManager) allows attaching to TransformNode
   * which properly transforms the entire model hierarchy
   * @param modelUrl - URL of the model to attach gizmo to
   */
  attachGizmoToModel(modelUrl: string): void {
    const root = this.modelComponent.getModelRoot(modelUrl);
    if (root) {
      // Clear any previous editing state
      this.editingPlaneIndex = -1;
      this.editingType = 'model';

      // Attach gizmo to the root TransformNode
      // This ensures the entire model hierarchy moves together
      this.modelEditorComponent.attachGizmoToNode(root);
      logger.debug('Gizmo attached to model root:', root.name, 'editingType:', this.editingType);
    } else {
      logger.warn('Model root not found for:', modelUrl);
    }
  }

  /**
   * Detach gizmo from current mesh
   */
  detachGizmo(): void {
    this.editingType = 'none';
    this.editingPlaneIndex = -1;
    this.modelEditorComponent.attachGizmoToMesh(null);
    logger.debug('Gizmo detached, editingType:', this.editingType);
  }

  /**
   * Set model transform directly without reloading the scene
   * Use this for real-time updates when editing with gizmos or number inputs
   * This keeps the gizmo attached and provides instant visual feedback
   * @param modelUrl - URL of the model to transform
   * @param position - New position (optional)
   * @param rotation - New rotation in radians (optional)
   * @param scale - New scale as x,y,z or uniform number (optional)
   * @returns true if transform was applied successfully
   */
  setModelTransform(
    modelUrl: string,
    position?: { x: number; y: number; z: number },
    rotation?: { x: number; y: number; z: number },
    scale?: { x: number; y: number; z: number } | number
  ): boolean {
    return this.modelComponent.setTransform(modelUrl, position, rotation, scale);
  }

  /**
   * Get current model transform from the 3D preview
   * This reads the actual transform values from the loaded model node
   * @param modelUrl - URL of the model to get transform from
   * @returns Current transform or null if model not loaded
   */
  getModelTransform(modelUrl: string): { position: { x: number; y: number; z: number }; rotation: { x: number; y: number; z: number }; scale: { x: number; y: number; z: number } } | null {
    const root = this.modelComponent.getModelRoot(modelUrl);
    if (!root) return null;

    return {
      position: {
        x: root.position.x,
        y: root.position.y,
        z: root.position.z,
      },
      rotation: {
        x: root.rotation.x,
        y: root.rotation.y,
        z: root.rotation.z,
      },
      scale: {
        x: root.scaling.x,
        y: root.scaling.y,
        z: root.scaling.z,
      },
    };
  }

  /**
   * Unload a specific model from the 3D preview
   * @param modelUrl - URL of the model to remove
   */
  unloadModel(modelUrl: string): void {
    this.modelComponent.unloadModel(modelUrl);
    logger.debug('Model unloaded:', modelUrl);
  }

  // ========== PLANE GIZMO METHODS ==========

  /**
   * Attach gizmo to a plane by index
   * Planes are regular Mesh objects so we can use the gizmo attachment directly
   * @param index - Index of the plane to attach gizmo to
   */
  attachGizmoToPlane(index: number): void {
    const plane = this.planeComponent.getPlane(index);
    if (plane) {
      this.editingPlaneIndex = index;
      this.editingType = 'plane';
      this.modelEditorComponent.attachGizmoToMesh(plane);
      logger.debug('Gizmo attached to plane:', index, 'editingType:', this.editingType);
    } else {
      logger.warn('Plane not found at index:', index);
    }
  }

  /**
   * Detach gizmo from current plane
   */
  detachPlaneGizmo(): void {
    this.editingPlaneIndex = -1;
    this.editingType = 'none';
    this.modelEditorComponent.attachGizmoToMesh(null);
    logger.debug('Plane gizmo detached');
  }

  /**
   * Set plane transform directly without reloading
   * @param index - Index of the plane to transform
   * @param position - New position (optional)
   * @param rotation - New rotation in radians (optional)
   * @param scale - New scale as width/height (optional)
   */
  setPlaneTransform(
    index: number,
    position?: { x: number; y: number; z: number },
    rotation?: { x: number; y: number; z: number },
    scale?: { width: number; height: number }
  ): boolean {
    return this.planeComponent.setTransform(index, position, rotation, scale);
  }

  /**
   * Get current plane transform from the 3D preview
   * @param index - Index of the plane
   */
  getPlaneTransform(index: number): { position: { x: number; y: number; z: number }; rotation: { x: number; y: number; z: number }; scale: { width: number; height: number } } | null {
    return this.planeComponent.getTransform(index);
  }

  /**
   * Get the currently editing plane index
   */
  getEditingPlaneIndex(): number {
    return this.editingPlaneIndex;
  }

  /**
   * Emit plane transform end event (called when gizmo drag ends)
   * This should be called by the gizmo callback handler
   */
  emitPlaneTransformEnd(): void {
    if (this.editingPlaneIndex >= 0 && this.onPlaneTransformEnd) {
      const transform = this.planeComponent.getTransform(this.editingPlaneIndex);
      if (transform) {
        this.onPlaneTransformEnd(this.editingPlaneIndex, transform);
      }
    }
  }

  // ========== INCREMENTAL UPDATE METHODS ==========

  /**
   * Set skybox rotation without full reload (rotates parent node only)
   * @param degrees - Rotation in degrees
   */
  setSkyboxRotation(degrees: number): void {
    const radians = (degrees * Math.PI) / 180;
    this.sceneContentParent.rotation.y = radians;
    this.domeComponent.resetRotation(); // Keep dome at local Math.PI
    logger.debug(`Skybox rotation set to ${degrees}° (incremental)`);
  }

  /**
   * Get current skybox rotation in degrees
   */
  getSkyboxRotation(): number {
    return (this.sceneContentParent.rotation.y * 180) / Math.PI;
  }

  /**
   * Get current camera alpha angle (horizontal rotation in radians)
   */
  getCameraAlpha(): number {
    return this.camera.alpha;
  }

  /**
   * Get current camera beta angle (vertical rotation in radians)
   */
  getCameraBeta(): number {
    return this.camera.beta;
  }

  /**
   * Set hotspot position without full reload
   * @param id - Hotspot ID
   * @param position - New position
   */
  setHotspotPosition(id: string, position: Vector3Config): void {
    const mesh = this.hotspotComponent.getHotspot(id);
    if (mesh) {
      mesh.position.set(position.x, position.y, position.z);
      logger.debug(`Hotspot ${id} position updated (incremental)`);
    }
    // Also update floor arrow if it exists
    const arrowMesh = this.floorArrowManager.getArrowMesh(id);
    if (arrowMesh) {
      arrowMesh.position.set(position.x, position.y, position.z);
      logger.debug(`Floor arrow ${id} position updated (incremental)`);
    }
  }

  /**
   * Set light property without full reload
   * @param index - Light index
   * @param prop - Property name (intensity, diffuse, specular, etc.)
   * @param value - New value
   */
  setLightProperty(index: number, prop: string, value: unknown): void {
    if (index >= 0 && index < this.currentLights.length) {
      const light = this.currentLights[index];
      switch (prop) {
        case 'intensity':
          light.intensity = value as number;
          break;
        case 'diffuse':
          light.diffuse = Color3.FromHexString(value as string);
          break;
        case 'specular':
          light.specular = Color3.FromHexString(value as string);
          break;
      }
      logger.debug(`Light ${index} ${prop} updated (incremental)`);
    }
  }

  /**
   * Set floor arrow direction without full reload
   * @param id - Hotspot/arrow ID
   * @param degrees - New arrow direction in degrees
   */
  setFloorArrowDirection(id: string, degrees: number): void {
    const arrowMesh = this.floorArrowManager.getArrowMesh(id);
    if (arrowMesh) {
      // Recalculate angle same as FloorArrowManager.createArrow
      const outwardAngle = Math.atan2(arrowMesh.position.x, arrowMesh.position.z);
      const userOffset = (degrees * Math.PI) / 180;
      arrowMesh.rotation.y = outwardAngle + userOffset;
      logger.debug(`Floor arrow ${id} direction set to ${degrees}° (incremental)`);
    }
  }

  /**
   * Set floor arrow color without full reload
   * @param id - Hotspot/arrow ID
   * @param hexColor - New color in hex format (e.g., '#00bfff')
   */
  setFloorArrowColor(id: string, hexColor: string): void {
    this.floorArrowManager.setArrowColor(id, hexColor);
  }

  /**
   * Set floor arrow rest scale without full reload
   * @param id - Hotspot/arrow ID
   * @param scale - New rest scale value
   */
  setFloorArrowRestScale(id: string, scale: number): void {
    this.floorArrowManager.setArrowRestScale(id, scale);
  }

  /**
   * Set floor arrow hover scale without full reload
   * @param id - Hotspot/arrow ID
   * @param scale - New hover scale value
   */
  setFloorArrowHoverScale(id: string, scale: number): void {
    this.floorArrowManager.setArrowHoverScale(id, scale);
  }

  // ========== VIEW DIRECTION TARGETS (draggable, like camera target) ==========

  private viewDirectionTargets: Mesh[] = [];

  /**
   * Create a crosshair target mesh for view direction (similar to camera target)
   */
  private createViewDirectionTargetMesh(color: string, index: number): Mesh {
    const size = 20;
    const mesh = MeshBuilder.CreatePlane(`viewDirTarget_${index}`, {
      size,
      sideOrientation: Mesh.DOUBLESIDE,
    }, this.scene);

    mesh.billboardMode = Mesh.BILLBOARDMODE_ALL;
    mesh.renderingGroupId = 2;
    mesh.isPickable = true;

    const material = new StandardMaterial(`viewDirTargetMat_${index}`, this.scene);

    const textureSize = 256;
    const texture = new DynamicTexture(`viewDirTargetTex_${index}`, textureSize, this.scene, true);
    const ctx = texture.getContext() as CanvasRenderingContext2D;

    ctx.clearRect(0, 0, textureSize, textureSize);

    const center = textureSize / 2;
    const outerRadius = textureSize * 0.4;
    const innerRadius = textureSize * 0.15;
    const lineWidth = 10;

    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';

    // Outer circle
    ctx.beginPath();
    ctx.arc(center, center, outerRadius, 0, Math.PI * 2);
    ctx.stroke();

    // Inner circle
    ctx.beginPath();
    ctx.arc(center, center, innerRadius, 0, Math.PI * 2);
    ctx.stroke();

    // Cross lines
    const gap = innerRadius + 5;
    const end = outerRadius - 5;
    ctx.beginPath();
    ctx.moveTo(center, center - gap); ctx.lineTo(center, center - end);
    ctx.moveTo(center, center + gap); ctx.lineTo(center, center + end);
    ctx.moveTo(center - gap, center); ctx.lineTo(center - end, center);
    ctx.moveTo(center + gap, center); ctx.lineTo(center + end, center);
    ctx.stroke();

    // Center dot
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(center, center, 5, 0, Math.PI * 2);
    ctx.fill();

    texture.update();
    texture.hasAlpha = true;

    const parsedColor = Color3.FromHexString(color);
    material.diffuseTexture = texture;
    material.emissiveTexture = texture;
    material.useAlphaFromDiffuseTexture = true;
    material.emissiveColor = parsedColor;
    material.backFaceCulling = false;

    mesh.material = material;
    mesh.metadata = { isViewDirectionTarget: true, indicatorIndex: index };

    // Parent to sceneContentParent so it rotates with the scene
    mesh.parent = this.sceneContentParent;

    return mesh;
  }

  /**
   * Show draggable view direction targets for incoming arrows to this stage
   * Each target represents where the camera will look on arrival from that arrow
   */
  showViewDirectionIndicators(indicators: Array<{ position: { x: number; y: number; z: number }; color: string; label: string }>): void {
    this.clearViewDirectionIndicators();

    const radius = 100; // Same distance as camera target

    indicators.forEach((indicator, index) => {
      const mesh = this.createViewDirectionTargetMesh(indicator.color, index);

      // Position at the normalized direction scaled to radius
      const dir = new Vector3(indicator.position.x, indicator.position.y, indicator.position.z).normalize();
      mesh.position = dir.scale(radius);

      this.viewDirectionTargets.push(mesh);
    });

    logger.debug(`Showing ${indicators.length} view direction target(s)`);
  }

  /**
   * Update a single view direction target's position
   */
  updateViewDirectionIndicator(index: number, position: { x: number; y: number; z: number }): void {
    if (index >= 0 && index < this.viewDirectionTargets.length) {
      const mesh = this.viewDirectionTargets[index];
      const dir = new Vector3(position.x, position.y, position.z).normalize();
      mesh.position = dir.scale(100);
    }
  }

  /**
   * Clear all view direction targets
   */
  clearViewDirectionIndicators(): void {
    this.viewDirectionTargets.forEach(mesh => {
      mesh.material?.dispose();
      mesh.dispose();
    });
    this.viewDirectionTargets = [];
  }

  /**
   * Show the human scale reference model (1.7m capsule)
   */
  showHumanScaleReference(): void {
    this.modelEditorComponent.showHumanScaleReference();
  }

  /**
   * Hide the human scale reference model
   */
  hideHumanScaleReference(): void {
    this.modelEditorComponent.hideHumanScaleReference();
  }

  /**
   * Attach position gizmo to human scale reference with Y locked to 0
   */
  attachGizmoToHumanScale(): void {
    this.editingType = 'humanScale';
    this.editingPlaneIndex = -1;
    this.modelEditorComponent.attachGizmoToHumanScale();
    logger.debug('Gizmo attached to human scale, editingType:', this.editingType);
  }

  /**
   * Detach gizmo from human scale reference
   */
  detachGizmoFromHumanScale(): void {
    this.editingType = 'none';
    this.modelEditorComponent.detachGizmoFromHumanScale();
    logger.debug('Human scale gizmo detached, editingType:', this.editingType);
  }

  /**
   * Set the human scale reference position (for VR spawn point)
   */
  setHumanScalePosition(position: { x: number; y: number; z: number }): void {
    this.modelEditorComponent.setHumanScalePosition(position);
  }

  /**
   * Get the human scale reference position (feet position)
   */
  getHumanScalePosition(): { x: number; y: number; z: number } | null {
    return this.modelEditorComponent.getHumanScalePosition();
  }

  /**
   * Set the human scale rotation (Y-axis, for spawn facing direction)
   * @param rotationY - Rotation in radians
   */
  setHumanScaleRotation(rotationY: number): void {
    this.modelEditorComponent.setHumanScaleRotation(rotationY);
  }

  /**
   * Get the human scale rotation (Y-axis)
   */
  getHumanScaleRotation(): number {
    return this.modelEditorComponent.getHumanScaleRotation();
  }

  // Callback for human scale rotation changes
  public onHumanScaleRotationChanged?: (rotationY: number) => void;

  // =========================================================================
  // GROUND PLANE
  // =========================================================================

  /**
   * Show a ground plane grid for spatial reference
   * @param size - Size of the ground plane (default 10 units)
   * @param gridDivisions - Number of grid divisions (default 10)
   */
  showGroundPlane(size: number = 10, gridDivisions: number = 10): void {
    this.modelEditorComponent.showGroundPlane(size, gridDivisions);
  }

  /**
   * Hide the ground plane
   */
  hideGroundPlane(): void {
    this.modelEditorComponent.hideGroundPlane();
  }

  /**
   * Check if ground plane is visible
   */
  isGroundPlaneVisible(): boolean {
    return this.modelEditorComponent.isGroundPlaneVisible();
  }

  /**
   * Show skeleton for a model
   */
  showModelSkeleton(modelUrl: string): void {
    const meshes = this.modelComponent.getMeshesForModel(modelUrl);
    for (const mesh of meshes) {
      if (mesh.skeleton) {
        this.modelEditorComponent.showSkeleton(modelUrl, mesh.skeleton, mesh);
        break; // Only show first skeleton found
      }
    }
  }

  /**
   * Hide skeleton for a model
   */
  hideModelSkeleton(modelUrl: string): void {
    this.modelEditorComponent.hideSkeleton(modelUrl);
  }

  /**
   * Check if skeleton is visible for a model
   */
  isSkeletonVisible(modelUrl: string): boolean {
    return this.modelEditorComponent.isSkeletonVisible(modelUrl);
  }

  /**
   * Get list of meshes for a model
   */
  getModelMeshList(modelUrl: string): MeshInfo[] {
    const root = this.modelComponent.getModelRoot(modelUrl);
    if (!root) return [];
    return this.modelEditorComponent.getMeshList(root);
  }

  /**
   * Set visibility for a mesh in a model
   */
  setModelMeshVisibility(modelUrl: string, meshName: string, visible: boolean): void {
    const root = this.modelComponent.getModelRoot(modelUrl);
    if (root) {
      this.modelEditorComponent.setMeshVisibility(root, meshName, visible);
    }
  }

  /**
   * Get statistics for a model
   */
  getModelStats(modelUrl: string): ModelStats | null {
    const root = this.modelComponent.getModelRoot(modelUrl);
    const meshes = this.modelComponent.getMeshesForModel(modelUrl);
    if (!root || meshes.length === 0) return null;
    return this.modelEditorComponent.getModelStatistics(root, meshes);
  }

  /**
   * Get list of animations for a model
   */
  getModelAnimations(modelUrl: string): AnimationInfo[] {
    const meshes = this.modelComponent.getMeshesForModel(modelUrl);
    return this.modelEditorComponent.getAnimationList(meshes);
  }

  /**
   * Play an animation by name
   */
  playAnimation(animationName: string, loop: boolean = true): void {
    this.modelEditorComponent.playAnimation(animationName, loop);
  }

  /**
   * Pause the current animation
   */
  pauseAnimation(): void {
    this.modelEditorComponent.pauseAnimation();
  }

  /**
   * Resume the current animation
   */
  resumeAnimation(): void {
    this.modelEditorComponent.resumeAnimation();
  }

  /**
   * Stop the current animation
   */
  stopAnimation(): void {
    this.modelEditorComponent.stopAnimation();
  }

  /**
   * Set animation playback speed
   */
  setAnimationSpeed(speed: number): void {
    this.modelEditorComponent.setAnimationSpeed(speed);
  }

  /**
   * Seek to a specific frame in the animation
   */
  seekAnimation(frame: number): void {
    this.modelEditorComponent.seekAnimation(frame);
  }

  /**
   * Get current animation state
   */
  getAnimationState(): AnimationState | null {
    return this.modelEditorComponent.getCurrentAnimationState();
  }

  // =========================================================================
  // POST-PROCESSING METHODS
  // =========================================================================

  /**
   * Apply a full post-processing configuration to the preview
   * This enables live preview of effects in the editor
   * @param config - PostProcessingConfig to apply
   */
  applyPostProcessing(config: PostProcessingConfig | undefined): void {
    if (!this.postProcessingManager) {
      logger.warn('PostProcessingManager not initialized');
      return;
    }
    this.postProcessingManager.applyConfig(config);
    logger.debug('Applied post-processing config');
  }

  /**
   * Update a single post-processing effect
   * @param effectName - Name of the effect to update
   * @param config - Effect configuration
   */
  updatePostProcessingEffect(
    effectName: keyof PostProcessingConfig,
    config: PostProcessingConfig[keyof PostProcessingConfig]
  ): void {
    if (!this.postProcessingManager) {
      logger.warn('PostProcessingManager not initialized');
      return;
    }
    this.postProcessingManager.updateEffect(effectName, config);
  }

  /**
   * Get the current post-processing configuration from the manager
   */
  getPostProcessingConfig(): PostProcessingConfig | null {
    if (!this.postProcessingManager) {
      return null;
    }
    return this.postProcessingManager.getConfig();
  }

  /**
   * Enable or disable bloom effect
   */
  setBloomEnabled(enabled: boolean): void {
    if (!this.postProcessingManager) return;
    this.postProcessingManager.updateEffect('bloom', { enabled });
  }

  /**
   * Set bloom properties
   */
  setBloomProperties(threshold?: number, weight?: number, kernel?: number, scale?: number): void {
    if (!this.postProcessingManager) return;
    this.postProcessingManager.updateEffect('bloom', {
      enabled: true,
      threshold,
      weight,
      kernel,
      scale
    });
  }

  /**
   * Enable or disable depth of field effect
   */
  setDepthOfFieldEnabled(enabled: boolean): void {
    if (!this.postProcessingManager) return;
    this.postProcessingManager.updateEffect('depthOfField', { enabled });
  }

  /**
   * Enable or disable FXAA antialiasing
   */
  setFxaaEnabled(enabled: boolean): void {
    if (!this.postProcessingManager) return;
    this.postProcessingManager.updateEffect('fxaa', enabled);
  }

  /**
   * Enable or disable chromatic aberration
   */
  setChromaticAberrationEnabled(enabled: boolean): void {
    if (!this.postProcessingManager) return;
    this.postProcessingManager.updateEffect('chromaticAberration', { enabled });
  }

  /**
   * Enable or disable film grain
   */
  setGrainEnabled(enabled: boolean): void {
    if (!this.postProcessingManager) return;
    this.postProcessingManager.updateEffect('grain', { enabled });
  }

  /**
   * Enable or disable vignette
   */
  setVignetteEnabled(enabled: boolean): void {
    if (!this.postProcessingManager) return;
    this.postProcessingManager.updateEffect('vignette', { enabled });
  }

  /**
   * Enable or disable sharpen
   */
  setSharpenEnabled(enabled: boolean): void {
    if (!this.postProcessingManager) return;
    this.postProcessingManager.updateEffect('sharpen', { enabled });
  }

  /**
   * Enable or disable SSAO (screen-space ambient occlusion)
   */
  setSSAOEnabled(enabled: boolean): void {
    if (!this.postProcessingManager) return;
    this.postProcessingManager.updateEffect('ssao', { enabled });
  }

  /**
   * Enable or disable glow layer
   */
  setGlowLayerEnabled(enabled: boolean): void {
    if (!this.postProcessingManager) return;
    this.postProcessingManager.updateEffect('glowLayer', { enabled });
  }

  /**
   * Set image processing properties (exposure, contrast, etc.)
   */
  setImageProcessing(config: PostProcessingConfig['imageProcessing']): void {
    if (!this.postProcessingManager) return;
    this.postProcessingManager.updateEffect('imageProcessing', config);
  }

  /**
   * Hide editor overlays (camera targets, view direction indicators) for clean screenshot capture
   * Returns a restore function to show them again
   */
  hideEditorOverlaysForCapture(): () => void {
    const hiddenMeshes: { mesh: Mesh; wasEnabled: boolean }[] = [];

    // Hide camera target mesh
    if (this.cameraTargetMesh) {
      hiddenMeshes.push({
        mesh: this.cameraTargetMesh,
        wasEnabled: this.cameraTargetMesh.isEnabled()
      });
      this.cameraTargetMesh.setEnabled(false);
    }

    // Hide view direction targets
    this.viewDirectionTargets.forEach(mesh => {
      hiddenMeshes.push({
        mesh,
        wasEnabled: mesh.isEnabled()
      });
      mesh.setEnabled(false);
    });

    // Return restore function
    return () => {
      hiddenMeshes.forEach(({ mesh, wasEnabled }) => {
        mesh.setEnabled(wasEnabled);
      });
    };
  }
}
